import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { VaultService } from 'src/app/services/vault.service';

import { ActivatedRoute } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { SubscriptionContainer } from 'src/app/models/subscription-container';
import { SocketService } from 'src/app/services/socket.service';
import { TeamsService } from 'src/app/services/teams.service';
import { DialogService } from '../../../services/dialog.service';

@Component({
    selector: 'app-vault',
    templateUrl: './vault.component.html',
    styleUrls: ['./vault.component.scss'],
    standalone: false
})
export class VaultComponent implements OnInit, OnDestroy, AfterViewChecked {
  private dialogs = inject(DialogService);
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChildren('editInput') editInputs: QueryList<ElementRef>;
  private focusPending = false;

  @Input() isWidget: boolean = false;
  @Input() teamName: string;
  @Input() parentId: string;
  @Output() playgroundClick = new EventEmitter<{ parentId: string; vaultName: string; workers: string[] }>();
  @Output() onEditFile = new EventEmitter<{ parentId: string; path: string }>();

  // Cache for notes content (vault name -> notes text or null)
  public notesCache: Map<string, string | null> = new Map();

  public dataSource = new MatTableDataSource<any>([]);
  public isLoading = true;

  private staticColumns = ['host', 'name', 'notes', 'runid', 'epoch', 'value', 'dateAdded', 'actions'];
  public perfColumns: string[] = [];
  public availableColumns: string[] = [];
  public displayedColumns = ['name', 'notes', 'runid', 'epoch', 'value', 'dateAdded', 'actions'];
  public columnFilter = '';

  private _subs = new SubscriptionContainer();

  // Inline editing state
  public editingRow: any = null;
  public editingField: string = null;
  public editValue: string = '';

  constructor(
    private activatedRoute: ActivatedRoute,
    private vaultService: VaultService,
    private _socketService: SocketService,
    private _teamsService: TeamsService
  ) {}

  ngOnInit(): void {
    if (this.isWidget) {
      this.load(this.teamName);
    } else {
      this.activatedRoute.params.subscribe((params) => {
        this.load(params.team);
      });
    }

    this._socketService.joinRoom('vault');
    this._subs.add = this._socketService.subscribeToRoomEvents('vault', () => {
      this.load(this.teamName);
    });
  }

  ngOnDestroy(): void {
    this._socketService.leaveRoom('vault');
    this._subs.dispose();
  }

  ngAfterViewChecked(): void {
    if (this.focusPending && this.editInputs.length > 0) {
      this.editInputs.first.nativeElement.focus();
      this.editInputs.first.nativeElement.select();
      this.focusPending = false;
    }
  }

  async load(teamName: string): Promise<void> {
    this.teamName = teamName;

    const sub = this.vaultService.load(this.teamName).subscribe((response) => {
      sub.unsubscribe();
      this.isLoading = false;

      if (!response || response.length == 0) return;

      const timestampColumns = ['dateAdded'];
      timestampColumns.forEach((col) => {
        response.forEach((row) => {
          if (row[col]) row[col] = new Date(row[col]);
        });
      });

      this.discoverPerfColumns(response);
      this.dataSource.data = response;

      // Load notes for each item
      response.forEach((row) => this.loadNotes(row.name));

      setTimeout(() => {
        this.dataSource.sort = this.sort;
      }, 0);
    });
  }

  private loadNotes(vaultName: string): void {
    if (this.notesCache.has(vaultName)) return;
    this.notesCache.set(vaultName, null); // Mark as loading
    const sub = this.vaultService.getNotes(this.teamName, vaultName).subscribe({
      next: (result) => {
        sub.unsubscribe();
        this.notesCache.set(vaultName, result.notes);
      },
      error: () => {
        sub.unsubscribe();
        this.notesCache.set(vaultName, null);
      }
    });
  }

  async openNotes(row: any): Promise<void> {
    const teamConfig = await this._teamsService.getConfigAsync(this.teamName);
    const notesPath = `${teamConfig.path.vault}/${row.name}/notes.md`;
    this.onEditFile.emit({ parentId: this.parentId, path: notesPath });
  }

  private discoverPerfColumns(response: any[]): void {
    const perfKeys = new Set<string>();
    const staticSet = new Set([...this.staticColumns, 'name', 'runid', 'epoch', 'dateAdded']);
    response.forEach((row) => {
      if (row.perf) {
        Object.keys(row.perf).forEach((key) => {
          if (staticSet.has(key)) return;
          if (typeof row.perf[key] === 'number') perfKeys.add(key);
        });
      }
    });

    this.perfColumns = Array.from(perfKeys);
    this.availableColumns = [...this.staticColumns, ...this.perfColumns];
  }

  public reloadData() {
    this.isLoading = true;
    this.load(this.teamName);
  }

  getPerfValue(row: any, col: string): string {
    const val = row.perf?.[col];
    if (val == null) return '';
    return typeof val === 'number' ? val.toFixed(4) : val;
  }

  formatColumnHeader(col: string): string {
    return col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  async serviceAction($event: MouseEvent, row: any, action: string) {
    if (action === 'playground') {
      this.playgroundClick.emit({ parentId: this.parentId, vaultName: row.name, workers: row.workers || [] });
      return;
    }
    if (action === 'delete') {
      $event.stopPropagation();
      if (!await this.dialogs.confirm(`Are you sure you want to delete ${row.name}?`)) return;
      const sub = this.vaultService.remove(this.teamName, row.name).subscribe(() => {
        sub.unsubscribe();
        this.load(this.teamName);
      });
      return;
    }
  }

  hasWorkers(row: any): boolean {
    return row.workers && row.workers.length > 0;
  }

  startEdit(row: any, field: string): void {
    // Prevent editing name if workers are deployed
    if (field === 'name' && this.hasWorkers(row)) return;

    this.editingRow = row;
    this.editingField = field;
    this.editValue = row[field] || '';
    this.focusPending = true;
  }

  cancelEdit(): void {
    this.editingRow = null;
    this.editingField = null;
    this.editValue = '';
  }

  saveEdit(row: any, field: string): void {
    if (!this.editingRow || this.editingField !== field) return;

    const newValue = this.editValue.trim();
    const oldValue = row[field] || '';

    // No change, just cancel
    if (newValue === oldValue) {
      this.cancelEdit();
      return;
    }

    if (field === 'name') {
      // Rename via API
      const sub = this.vaultService.rename(this.teamName, oldValue, newValue).subscribe({
        next: () => {
          sub.unsubscribe();
          row.name = newValue;
          this.cancelEdit();
        },
        error: (err) => {
          console.error('Rename failed:', err);
          this.cancelEdit();
        }
      });
    } else if (field === 'value') {
      // Update value via API
      const cleanValue = newValue.replace(/[$,]/g, '');
      row.value = cleanValue;
      const sub = this.vaultService.update(this.teamName, row).subscribe({
        next: () => {
          sub.unsubscribe();
          this.cancelEdit();
        },
        error: (err) => {
          console.error('Update failed:', err);
          this.cancelEdit();
        }
      });
    }
  }
}
